import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Upload, Sparkles, Package, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';

function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'lunchbox',
    age_range: '3-6',
    price: '',
    original_price: '',
    colors: '',
    materials: '',
    features: '',
    image_urls: [],
    stock_count: 100,
  });
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await api.getProducts();
      setProducts(data.products || []);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      id: product.id,
      title: product.title,
      description: product.description,
      category: product.category,
      age_range: product.age_range,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      colors: product.colors?.join(', ') || '',
      materials: product.materials?.join(', ') || '',
      features: product.features?.join(', ') || '',
      image_urls: product.image_urls || [],
      stock_count: product.stock_count || 100,
    });
    setShowDialog(true);
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      category: 'lunchbox',
      age_range: '3-6',
      price: '',
      original_price: '',
      colors: '',
      materials: '',
      features: '',
      image_urls: [],
      stock_count: 100,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const productData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        age_range: formData.age_range,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        colors: formData.colors ? formData.colors.split(',').map(c => c.trim()) : [],
        materials: formData.materials ? formData.materials.split(',').map(m => m.trim()) : [],
        features: formData.features ? formData.features.split(',').map(f => f.trim()) : [],
        image_urls: formData.image_urls,
        stock_count: parseInt(formData.stock_count),
      };

      if (formData.id) {
        await api.adminUpdateProduct(formData.id, productData, token);
        toast.success('Product updated successfully!');
      } else {
        await api.adminCreateProduct(productData, token);
        toast.success('Product created successfully!');
      }

      setShowDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.adminDeleteProduct(productId, token);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleImageUploadForAI = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setExtracting(true);
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Image = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
          
          toast.info('🤖 AI is analyzing the image...');
          const result = await api.adminAIExtract(base64Image, token);
          
          if (result.success && result.data) {
            // Get the uploaded image URL from the response
            const uploadedImageUrl = result.uploaded_image_url;
            
            // Auto-fill form with AI extracted data AND the uploaded image
            setFormData({
              ...formData,
              title: result.data.title || formData.title,
              description: result.data.description || formData.description,
              category: result.data.category || formData.category,
              age_range: result.data.age_range || formData.age_range,
              price: result.data.price_suggestion?.toString() || formData.price,
              colors: result.data.colors?.join(', ') || formData.colors,
              materials: result.data.materials?.join(', ') || formData.materials,
              features: result.data.features?.join(', ') || formData.features,
              // Add the uploaded image to image_urls array
              image_urls: uploadedImageUrl ? [uploadedImageUrl, ...formData.image_urls] : formData.image_urls,
            });
            
            if (uploadedImageUrl) {
              toast.success('✨ AI extraction successful! Image uploaded and data extracted.');
            } else {
              toast.success('✨ AI extraction successful! Review and adjust the data. Note: Image upload failed, please upload manually.');
            }
          } else {
            toast.error(result.detail || 'Failed to extract product info');
          }
        } catch (error) {
          console.error('AI extraction error:', error);
          toast.error('AI extraction failed. Please try again.');
        } finally {
          setExtracting(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to process image');
      setExtracting(false);
    }
  };

  const handleFixStock = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/fix-stock`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to fix stock');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Products</h1>
            <p className="text-muted-foreground">Manage your product catalog</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleFixStock} title="Fix products showing out of stock incorrectly">
              Fix Stock Status
            </Button>
            <Button onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : (
          <>
            {/* Stock Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">In Stock</span>
                </div>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  {products.filter(p => p.in_stock && (p.stock_count === undefined || p.stock_count > 0)).length}
                </p>
              </Card>
              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">Low Stock (≤5)</span>
                </div>
                <p className="text-2xl font-bold text-yellow-700 mt-1">
                  {products.filter(p => p.stock_count > 0 && p.stock_count <= 5).length}
                </p>
              </Card>
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">Out of Stock</span>
                </div>
                <p className="text-2xl font-bold text-red-700 mt-1">
                  {products.filter(p => !p.in_stock || p.stock_count <= 0).length}
                </p>
              </Card>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => {
                const stockCount = product.stock_count ?? 100;
                const isLowStock = stockCount > 0 && stockCount <= 5;
                const isOutOfStock = !product.in_stock || stockCount <= 0;
                
                return (
                  <Card key={product.id} className={`overflow-hidden ${isOutOfStock ? 'opacity-60' : ''}`}>
                    <div className="relative">
                      <img
                        src={product.image_urls[0] || 'https://via.placeholder.com/300'}
                        alt={product.title}
                        className="w-full h-48 object-cover"
                      />
                      {isOutOfStock && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Badge className="bg-red-500 text-white text-lg px-4 py-2">OUT OF STOCK</Badge>
                        </div>
                      )}
                      {isLowStock && !isOutOfStock && (
                        <Badge className="absolute top-2 right-2 bg-yellow-500 text-white">
                          Low Stock: {stockCount}
                        </Badge>
                      )}
                    </div>
                    <div className="p-4 space-y-3">
                      <h3 className="font-semibold line-clamp-2">{product.title}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold">₹{product.price}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            isOutOfStock ? 'bg-red-100 text-red-800' : 
                            isLowStock ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-green-100 text-green-800'
                          }`}>
                            Stock: {stockCount}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleEdit(product)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Edit/Add Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{formData.id ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* AI Product Extraction */}
              <Card className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-600 rounded-lg">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                      AI Product Extraction
                    </h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                      Upload a product image and let AI automatically extract product details
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUploadForAI}
                        disabled={extracting}
                        className="hidden"
                        id="ai-image-upload"
                      />
                      <label htmlFor="ai-image-upload">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={extracting}
                          className="cursor-pointer bg-white dark:bg-gray-800"
                          asChild
                        >
                          <span>
                            {extracting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload & Extract
                              </>
                            )}
                          </span>
                        </Button>
                      </label>
                      {extracting && (
                        <span className="text-sm text-purple-600 dark:text-purple-400 animate-pulse">
                          AI is reading the image...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              <div>
                <Label htmlFor="title">Product Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Rainbow Unicorn Lunchbox"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Leak-proof, colorful lunchbox..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="lunchbox">Lunchbox</option>
                    <option value="bag">School Bag</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="age_range">Age Range *</Label>
                  <select
                    id="age_range"
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                    value={formData.age_range}
                    onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                  >
                    <option value="3-6">3-6 years</option>
                    <option value="7-10">7-10 years</option>
                    <option value="11-15">11-15 years</option>
                    <option value="3-12">3-12 years (All Kids)</option>
                    <option value="5-10">5-10 years</option>
                    <option value="6-12">6-12 years</option>
                    <option value="all">All Ages</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Price (₹) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="499"
                  />
                </div>
                <div>
                  <Label htmlFor="original_price">Original Price (₹)</Label>
                  <Input
                    id="original_price"
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="699"
                  />
                </div>
                <div>
                  <Label htmlFor="stock_count">Stock</Label>
                  <Input
                    id="stock_count"
                    type="number"
                    value={formData.stock_count}
                    onChange={(e) => setFormData({ ...formData, stock_count: e.target.value })}
                    placeholder="100"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="colors">Colors (comma-separated)</Label>
                <Input
                  id="colors"
                  value={formData.colors}
                  onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                  placeholder="Pink, Purple, White"
                />
              </div>

              <div>
                <Label htmlFor="materials">Materials (comma-separated)</Label>
                <Input
                  id="materials"
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  placeholder="BPA-free plastic, Stainless steel"
                />
              </div>

              <div>
                <Label htmlFor="features">Features (comma-separated)</Label>
                <Input
                  id="features"
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  placeholder="Leak-proof seal, Easy to clean"
                />
              </div>

              {/* Image URLs Section */}
              <div>
                <Label>Product Images</Label>
                <div className="mt-2 space-y-3">
                  {/* Image Preview */}
                  {formData.image_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.image_urls.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Product ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newUrls = formData.image_urls.filter((_, i) => i !== index);
                              setFormData({ ...formData, image_urls: newUrls });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Image URL Input */}
                  <div className="flex gap-2">
                    <Input
                      id="new_image_url"
                      placeholder="Enter image URL or use AI extraction above"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          setFormData({
                            ...formData,
                            image_urls: [...formData.image_urls, e.target.value.trim()]
                          });
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById('new_image_url');
                        if (input.value.trim()) {
                          setFormData({
                            ...formData,
                            image_urls: [...formData.image_urls, input.value.trim()]
                          });
                          input.value = '';
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tip: Use AI extraction above to auto-upload and add images, or paste Cloudflare image URLs here
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    formData.id ? 'Update Product' : 'Create Product'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminProductsPage;
